/**
 * Print Area Configuration
 *
 * Quality and unit conversion utilities for print area calculations.
 *
 * NOTE: For print area dimensions, use PrintContext from types/printContext.ts
 * The PrintContext provides a Single Source of Truth for all print area calculations.
 *
 * @see types/printContext.ts - PrintContext interface
 * @see stores/productSelectionStore.ts - activePrintContext
 * @see hooks/usePrintContext.ts - Hook for accessing PrintContext
 */

/**
 * DPI quality thresholds for print quality assessment
 */
export const DPI_THRESHOLDS = {
  POOR: 72, // Below this = poor quality
  MEDIUM: 150, // 72-150 = medium quality
  GOOD: 300, // 150-300 = good quality
  EXCELLENT: 300, // Above this = excellent quality
} as const;

/**
 * Quality level definitions
 */
export type QualityLevel = 'poor' | 'medium' | 'good' | 'excellent';

export interface QualityInfo {
  level: QualityLevel;
  dpi: number;
  color: string;
  labelKey: string; // i18n key
}

/**
 * Get quality info based on DPI
 */
export function getQualityInfo(dpi: number): QualityInfo {
  if (dpi < DPI_THRESHOLDS.POOR) {
    return {
      level: 'poor',
      dpi,
      color: '#EF4444', // red-500
      labelKey: 'quality.poor',
    };
  }
  if (dpi < DPI_THRESHOLDS.MEDIUM) {
    return {
      level: 'medium',
      dpi,
      color: '#F59E0B', // amber-500
      labelKey: 'quality.medium',
    };
  }
  if (dpi < DPI_THRESHOLDS.GOOD) {
    return {
      level: 'good',
      dpi,
      color: '#22C55E', // green-500
      labelKey: 'quality.good',
    };
  }
  return {
    level: 'excellent',
    dpi,
    color: '#22C55E', // green-500
    labelKey: 'quality.excellent',
  };
}

/**
 * Quality labels (for display, will be replaced with i18n)
 */
export const QUALITY_LABELS: Record<QualityLevel, { en: string; de: string }> =
  {
    poor: { en: 'Poor Quality', de: 'Schlechte Qualität' },
    medium: { en: 'Medium Quality', de: 'Mittlere Qualität' },
    good: { en: 'Good Quality', de: 'Gute Qualität' },
    excellent: { en: 'Excellent Quality', de: 'Sehr gute Qualität' },
  };

/**
 * Unit system based on locale
 */
export type UnitSystem = 'metric' | 'imperial';

/**
 * Get unit system based on locale
 */
export function getUnitSystem(locale: string): UnitSystem {
  // US and UK typically use imperial
  if (locale.startsWith('en-US') || locale.startsWith('en-GB')) {
    return 'imperial';
  }
  return 'metric';
}

/**
 * Convert mm to the appropriate unit based on locale
 */
export function convertFromMm(
  mm: number,
  unitSystem: UnitSystem
): { value: number; unit: string } {
  if (unitSystem === 'imperial') {
    return {
      value: mm / 25.4, // mm to inches
      unit: 'in',
    };
  }
  return {
    value: mm / 10, // mm to cm
    unit: 'cm',
  };
}
